import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { ANGULAR_WRAPPER_ID } from '../constants';
import { catchError } from 'rxjs/operators';
import { RpcError } from '../shared/error/rpc.error';
import { RpcService } from '../shared/http/rpc.service';
import { TokenService } from './token.service';
import { IntercomService } from 'app/shared/intercom/intercom.service';
import { environment } from 'environments/environment';
import { Store } from '@ngrx/store';
import { Logout } from '../store';
import { SessionService } from '../shared/session/session.service';
import { HttpClient } from '@angular/common/http';
import { WindowService } from 'app/shared/browser/window.service';

interface LogoutResponse {
  redirectUrl: string;
}

@Injectable()
export class AuthenticationService {
  public isLoggedInAsPartner$: Observable<boolean>;

  private _partnerApiToken: string;
  private location: Location;
  private isLoggedInAsPartnerSubject: BehaviorSubject<
    boolean
  > = new BehaviorSubject(false);

  constructor(
    private rcpService: RpcService,
    private httpClient: HttpClient,
    private tokenService: TokenService,
    private windowService: WindowService,
    private intercom: IntercomService,
    private store: Store<any>,
    private session: SessionService
  ) {
    this.location = this.windowService.nativeWindow.location;
    this._partnerApiToken = sessionStorage.getItem('partnerapitoken');
    this.isLoggedInAsPartner$ = this.isLoggedInAsPartnerSubject.asObservable();
    this.isLoggedInAsPartnerSubject.next(this.isLoggedInAsPartner());
    sessionStorage.setItem(ANGULAR_WRAPPER_ID, ANGULAR_WRAPPER_ID);
  }

  public isLoggedInAsPartner(): boolean {
    if (environment.enableSSO) {
      return this.tokenService.getApplication() === 'partner';
    }
    return !!this._partnerApiToken;
  }

  public get partnerApiToken(): string {
    return this._partnerApiToken;
  }

  public logout(): void {
    const redirectUrl = this.getLogoutRedirectUrl();
    this.httpClient
      .post<LogoutResponse>(
        `/PosServer/rest/internal/v1/sso/signout/manager`,
        null,
        { params: { redirect_uri: redirectUrl.href } }
      )
      .subscribe(response => {
        this.intercom.logOut();

        if (!environment.enableSSO) {
          this.store.dispatch(new Logout());
          this.tokenService.token = null;
          this.tokenService.adminToken = null;
        }

        sessionStorage.removeItem('Establishments');

        this.location.href = response.redirectUrl;
      });
  }

  private getLogoutRedirectUrl(): URL {
    const redirectUrl = new URL(
      `${this.location.origin.replace('-web', '')}${environment.authService}`
    );
    if (this.location.hostname && this.location.hostname === 'localhost') {
      redirectUrl.searchParams.append('local', 'true');
    }

    if (this.session.isComingFromApp()) {
      redirectUrl.searchParams.append('loginFrom', 'pos');
    }
    return redirectUrl;
  }

  public login(credentials: any, companyId: number): Observable<string> {
    if (companyId) {
      return this.authenticateUser(
        credentials.username,
        credentials.password,
        companyId
      );
    } else {
      return this.authenticateAdmin(credentials.username, credentials.password);
    }
  }

  public loginSSO(): Observable<string> {
    return this.tokenService.getToken$(false);
  }

  // companyId is provided via query params from ios-app or other
  // usernames for non-admin are not unique, so we need the companyId to try to login with the good user.
  // if it fails (wrong companyId or wrong credentials) or if you don't have a companyId, because admin username are unique, then try to login as an admin without companyId
  private authenticate(
    params: Array<any>,
    company?: boolean
  ): Observable<string> {
    const method: string = company ? 'getTokenFromCompany' : 'getToken';
    return this.rcpService.makeGenericCall('manager.' + method, params);
  }

  private authenticateAdmin(
    username: string,
    password: string
  ): Observable<string> {
    const params: Array<string> = [username, password, 'webmanager'];
    return this.authenticate(params, false);
  }

  private authenticateUser(
    username: string,
    password: string,
    companyId: number
  ): Observable<string> {
    const params: Array<any> = [
      username,
      password,
      companyId,
      'webmanager',
      '',
      '0.1'
    ];
    return this.authenticate(params, true).pipe(
      catchError((error: RpcError) => {
        if (error.code === 1001 && error.msg === 'token has expired') {
          return this.authenticateAdmin(username, password);
        } else {
          throw error;
        }
      })
    );
  }
}
